import { Autocomplete, TextField } from '@mui/material';
import React, { useMemo } from 'react';
import { PopupSubtitle } from './BasePopup.jsx';
import Chip from '@mui/material/Chip';
import { useProperty } from '@olympeio/core';
import { AdvancedSearchKey } from './_enums';
import debounce from 'lodash.debounce';

// prettier-ignore
export const AdvancedMasterDataSearchInput = React.forwardRef(({context,data, logger, getOptions, path, filter, onChange, textFieldLabel, searchKey,title=searchKey, placeholderText = 'Search By ID Or Name', ...other}, ref) => {
    const [value, setValue] = React.useState(null);
    const [pasteQuery, setPasteQuery] = React.useState(false);

    const [loading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const selectedContextKey = `Selected ${searchKey}`;
    const selectedOptions = useProperty(context, selectedContextKey, false) || [];
    // eslint-disable-next-line no-unused-vars
    const [total, setTotal] = React.useState(0);
    const [userInput, setUserInput] = React.useState('');

      const querySearchValues = (text, pasted) => {
          const [controlFlow, pathInput, filtersInput] = getOptions.getInputs();

          const [successOutput, resultsOutput, errorOutput] = getOptions.getOutputs();


          const getOptionsRunner = context
              .runner(getOptions)
              .set(pathInput, path)
              .set(filtersInput, filter(text))
              .trigger(controlFlow);

          getOptionsRunner
              .waitFor(successOutput)
              .then(() => {
                  setOptions(getOptionsRunner.get(resultsOutput));
                  // if pasted and only one exact match is found, apply immediately
                  if (pasted) {
                      const results = getOptionsRunner.get(resultsOutput);
                      if (results.length === 1 && results[0].PosIdName === text) {
                        setOptions(results)
                      }
                      setPasteQuery(false);
                  }
                  setLoading(false);
              })
              .catch((error) => {
                  console.error('getposition: wait for error rejected with', error);
              });
          getOptionsRunner
              .waitFor(errorOutput)
              .then(() => {
                  setPasteQuery(false);
                  setLoading(false);
              })
              .catch((error) => {
                  console.error('getposition: wait for error rejected with', error);
              });
      };
      // debounce query and keep same callback between react renders (see https://dmitripavlutin.com/react-throttle-debounce/)
      const debouncedQuery = useMemo(() => debounce(querySearchValues, 300), []);


    const onChangeHandler = (e, newInputValue, reason) => {
       if(reason === 'reset'){
         setUserInput('');
       } else {
         setUserInput(newInputValue);
       }

       if(reason === 'input' && newInputValue.length > 1) {
        setLoading(true);
        debouncedQuery(newInputValue, pasteQuery);

       }

   
    };

    const pushNewSelectedValue = (newValue) => {
        const key = getOptionLabel(newValue);
        if (selectedOptions.find((opt) => getOptionLabel(opt) === key) === undefined) {
            context.set(selectedContextKey, [...selectedOptions, newValue]);
        }
    };

    const onSelectHandler = (event, newValue) => {
        setValue('');
        setOpen(false);
        setUserInput('');
        pushNewSelectedValue(newValue);

    };

    const onRemoveFromList = (idx, e) => {
        context.set(
            selectedContextKey,
            selectedOptions.filter((item, index) => index !== idx)
        );
    };

 context.observe(`Clear ${searchKey}`, true).subscribe((value) => {
        if (value !== null) {
            context.remove(`Clear ${searchKey}`); // prevent loops upon re-rendering
            context.set(selectedContextKey, []);
            context.set(searchKey, []);
            setUserInput('');
            setValue(null);
        }
    });

    let getOptionLabel;
    // label consisting of the object ID and name, ID must be included so that each entry has an unique key in the Dropdown list
    if (searchKey === AdvancedSearchKey.APPROVER) {
        getOptionLabel = (item) => `${item.ApproverId} - ${item.ApproverName}`;
    } else if (searchKey === AdvancedSearchKey.AGENT) {
        getOptionLabel = (item) => `${item.PosIdName} (${item.PosId})`;
    } else if (searchKey === AdvancedSearchKey.COST_CENTER) {
        getOptionLabel = (item) => `${item.CostCenterId} - ${item.CostCenterDescription}`;
    } else if (searchKey === AdvancedSearchKey.PURCHASE_GROUP) {
        getOptionLabel = (item) => `${item.PurchGroup} - ${item.PurchGroupName}`;
    } else if (searchKey === AdvancedSearchKey.PRN_BY_WBS_ELEMENT) {
        getOptionLabel = (item) => `${item.WbsElement} (PRN ${item.PrnNo})`;
    } else {
        // cases PrnByInternal and PrnByNumber
        getOptionLabel = (item) => item.PrnNo + (item.PrnName ? `- ${item.PrnName}` : '');
    }

    return (
        <div className={'advanced-search-input-container'} style={{ marginBottom: '4vh' }}>
            <PopupSubtitle subtitle={title} />
            <Autocomplete
                className="popup-input"
                value={value}
                inputValue={userInput}
                onChange={onSelectHandler}
                open={open}
                loading={loading}
                onInputChange={onChangeHandler}
                onPaste={() => {
                    context.set(searchKey, []); // force reset "data" variable in case of pasting, deleting the value, and pasting again
                    setPasteQuery(true); // query will trigger through onInputChange
                }}
                onBlur={() => {
                    setOpen(false);
                }}
                options={options ?? []}
                getOptionLabel={(item) => {
                    if (item) {
                        return getOptionLabel(item);
                    } else {
                        return '';
                    }
                }}
                disableClearable={true}
                size={'small'}
                renderInput={(params) => <TextField {...params} placeholder={placeholderText} variant="standard" />}
                onOpen={() => {
                    setOpen(true);
                }}
                onClose={() => {
                    setOpen(false);
                }}
                forcePopupIcon={true}
                clearOnBlur={false}
                ref={ref}
                {...other}
            />
            <div className={'advanced-search-chip-array'} style={{ marginTop: '1vh', height: 'fit-content' }}>
                {selectedOptions.map((item, idx) => (
                    <Chip
                        key={`chip-${searchKey}-${idx}`}
                        className="advanced-search-chip"
                        label={getOptionLabel(item)}
                        color="primary"
                        variant="outlined"
                        onDelete={(e) => onRemoveFromList(idx, e)}
                    />
                ))}
            </div>
        </div>
    );
});

/**
 * React hook to update a value based on a delay (debounced)
 * @param {string} value
 * @param {number} delay - in milliseconds
 * @returns {string}
 */
